import React from 'react';
import { useTranslation } from 'react-i18next';
import { ConverterType, ConversionResult, ImportResult, PreviewTransaction, CurrencyType } from '../types';
import TransactionPreview from './TransactionPreview';
import comingSoonBankIds from '../data/comingSoonBankIds.json';

interface FileConverterProps {
  activeConverter: ConverterType;
  selectedAccountId?: string | null;
  isLoading: boolean;
  error: string | null;
  result: ConversionResult | null;
  previewTransactions: PreviewTransaction[];
  targetCurrency: CurrencyType;
  importResult: ImportResult | null;
  isDragOver: boolean;
  onFileUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onDrop: (event: React.DragEvent<HTMLDivElement>) => void;
  onDragOver: (event: React.DragEvent<HTMLDivElement>) => void;
  onDragEnter: (event: React.DragEvent<HTMLDivElement>) => void;
  onDragLeave: (event: React.DragEvent<HTMLDivElement>) => void;
  onDownload: () => void;
  onReset: () => void;
  onImport: () => void;
  onToggleTransaction: (id: string) => void;
  onSelectAllTransactions: (selected: boolean) => void;
}

const FileConverter: React.FC<FileConverterProps> = ({
  activeConverter,
  selectedAccountId,
  isLoading,
  error,
  result,
  previewTransactions,
  targetCurrency,
  importResult,
  isDragOver,
  onFileUpload,
  onDrop,
  onDragOver,
  onDragEnter,
  onDragLeave,
  onDownload,
  onReset,
  onImport,
  onToggleTransaction,
  onSelectAllTransactions,
}) => {
  const { t } = useTranslation();
  const selectedCount = previewTransactions.filter((transaction) => transaction.selected).length;
  const hasSelection = selectedCount > 0;
  
  const getConverterTitle = () => {
    switch (activeConverter) {
      case 'alrajhi': return t('alrajhi.title', { ns: 'converter' });
      case 'wise': return t('wise.title', { ns: 'converter' });
      case 'cib': return t('cib.title', { ns: 'converter' });
      case 'category': return t('category.title', { ns: 'converter' });
      case 'other': return t('other.title', { ns: 'converter' });
      default: return t('default.title', { ns: 'converter' });
    }
  };

  const getUploadText = () => {
    switch (activeConverter) {
      case 'alrajhi': return t('alrajhi.upload', { ns: 'converter' });
      case 'wise': return t('wise.upload', { ns: 'converter' });
      case 'cib': return t('cib.upload', { ns: 'converter' });
      case 'category': return t('category.upload', { ns: 'converter' });
      case 'other': return t('other.upload', { ns: 'converter' });
      default: return t('default.upload', { ns: 'converter' });
    }
  };

  const getUploadDescription = () => {
    switch (activeConverter) {
      case 'alrajhi': return t('alrajhi.description', { ns: 'converter' });
      case 'wise': return t('wise.description', { ns: 'converter' });
      case 'cib': return t('cib.description', { ns: 'converter' });
      case 'category': return t('category.description', { ns: 'converter' });
      case 'other': return t('other.description', { ns: 'converter' });
      default: return t('default.description', { ns: 'converter' });
    }
  };

  const getFileAccept = () => {
    // For "other" converter, only accept CSV files since AI inference only works with CSV
    if (activeConverter === 'other') {
      return '.csv';
    }
    return activeConverter === 'alrajhi' ? '.xls,.xlsx' : '.csv';
  };

  // Show message when no converter is selected
  if (!activeConverter) {
    return (
      <div className="converter-container">
        <div className="upload-section">
          <div className="upload-area">
            <div className="upload-icon">{selectedAccountId ? '🔗' : '📍'}</div>
            <h4>
              {selectedAccountId
                ? t('configureLinkedAccount', { ns: 'converter' })
                : t('selectCountryAndBank', { ns: 'converter' })}
            </h4>
            <p>
              {selectedAccountId
                ? t('configureLinkedAccountDesc', { ns: 'converter' })
                : t('selectCountryAndBankDesc', { ns: 'converter' })}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show coming soon message for unsupported converters (but not for 'other')
  const isComingSoon = comingSoonBankIds.includes(activeConverter);
  if (isComingSoon) {
    return (
      <div className="converter-container">
        <div className="coming-soon-message">
          <h3>{t('comingSoon', { ns: 'converter' })}</h3>
          <p>{t('comingSoonDesc', { ns: 'converter' })}</p>
          <p>{t('comingSoonFallback', { ns: 'converter' })}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="converter-container">
      <h3>{getConverterTitle()}</h3>
      
      {!result && (
        <div className="upload-section">
          <div 
            className={`upload-area ${isDragOver ? 'drag-over' : ''}`}
            onDragOver={onDragOver}
            onDragEnter={onDragEnter}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
          >
            <div className="upload-icon">
              {activeConverter === 'other' ? '🤖' : '📄'}
            </div>
            <h4>{getUploadText()}</h4>
            <p>{getUploadDescription()}</p>
            
            <label htmlFor="file-input" className="upload-button">
              {isLoading ? t('converting', { ns: 'converter' }) : t('chooseFile', { ns: 'converter' })}
            </label>
            <input
              id="file-input"
              type="file"
              accept={getFileAccept()}
              onChange={onFileUpload}
              disabled={isLoading}
              style={{ display: 'none' }}
            />
          </div>

          {isLoading && (
            <div className="loading">
              <div className="spinner"></div>
              <p>
                {activeConverter === 'other' 
                  ? t('convertingFile', { ns: 'converter' }) + ' (AI analyzing format...)'
                  : t('convertingFile', { ns: 'converter' })
                }
              </p>
            </div>
          )}

          {error && (
            <div className="error">
              <h4>{t('error', { ns: 'converter' })}</h4>
              <p>{error}</p>
            </div>
          )}
        </div>
      )}

      {result && (
        <div className="result-section">
          <div className="success">
            <h4>{t('successMessage', { ns: 'converter' })}</h4>
            <div className="result-info">
              <p><strong>{t('transactionsProcessed', { ns: 'converter' })}</strong> {result.transactionCount}</p>
              <p><strong>{t('transactionsSelected', { ns: 'converter' })}</strong> {selectedCount}</p>
              <p><strong>{t('outputFile', { ns: 'converter' })}</strong> {result.filename}</p>
              {activeConverter === 'other' && result.bankName && (
                <p><strong>{t('detectedFormat', { ns: 'converter' })}</strong> {result.bankName}</p>
              )}
            </div>

            {previewTransactions.length > 0 && (
              <TransactionPreview
                transactions={previewTransactions}
                targetCurrency={targetCurrency}
                onToggleTransaction={onToggleTransaction}
                onSelectAll={onSelectAllTransactions}
              />
            )}
            
            <div className="action-buttons">
              <button 
                onClick={onImport} 
                className="import-button primary-action"
                disabled={!hasSelection}
              >
                {t('importButton', { ns: 'converter' })}
              </button>
              <div className="secondary-actions">
                <button
                  onClick={onDownload}
                  className="download-button secondary-action"
                  disabled={!hasSelection}
                >
                  {t('downloadButton', { ns: 'converter' })}
                </button>
                <button onClick={onReset} className="reset-button secondary-action">
                  {t('convertAnother', { ns: 'converter' })}
                </button>
              </div>
            </div>
          </div>
          
          {importResult && (
            <div className="import-result">
              <h5>{t('importResults', { ns: 'converter' })}</h5>
              <div className="import-info">
                <p><strong>{t('transactionsImported', { ns: 'converter' })}</strong> {importResult.imported}</p>
                {importResult.errors.length > 0 && (
                  <div className="import-errors">
                    <p><strong>{t('warningsErrors', { ns: 'converter' })}</strong></p>
                    <ul>
                      {importResult.errors.map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FileConverter; 